import { Roles } from "@outschool/auth-shared";
import { Box } from "@outschool/backpack";
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  activityApprovalPath,
  activitySchedulePath,
  clubManageListingPath,
  searchPath,
  sectionCharterSchoolEnrollPath,
  sectionEnrollPath,
} from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useProductViewed } from "@outschool/ui-analytics";
import {
  EsaSessionType,
  useCurrentUserHasRole,
  useEsaWaitPeriod,
  useFundingProgramAbbr,
  useSession,
} from "@outschool/ui-auth";
import { useTrackClassPageImpression } from "@outschool/ui-components-website";
import { Container, Space } from "@outschool/ui-legacy-component-library";
import {
  Redirect,
  Screen,
  lazyWithRetry,
  useNavigation,
  useScrollToElement,
} from "@outschool/ui-utils";
import React, { useEffect } from "react";
import { Route, Switch, useLocation, useRouteMatch } from "react-router-dom";

import * as Activity from "../../../shared/Activity";
import * as User from "../../../shared/User";
import { isOrganizationAdminWhoCanEnroll } from "../../lib/Users";
import ActivityEnrollPage from "../../routes/ActivityEnrollPage";
import EnrollmentMeetingPurchasePage from "../../routes/EnrollmentMeetingPurchasePage";
import { useAppState } from "../../stores/AppStateProvider";
import ActivityStatusLabel from "../ActivityStatusLabel";
import { ClassScheduleRequestModal } from "../ClassScheduleRequest";
import SimpleLayout from "../layouts/SimpleLayout";
import Link from "../Link";
import ActivityMetaTags from "../sharing/ActivityMetaTags";
import Toolbar from "../Toolbar";
import useFetchEnrollableTimes from "./hooks/useFetchEnrollableTimes";
import ClassDetailsMainBody from "./mainBody/ClassDetailsMainBody";
import { MobileBelowClassDetail } from "./mobileBelowClassDetail/MobileBelowClassDetail";
import { DefaultBelowClassDetail } from "./subcomponents/DefaultBelowClassDetail";
import MobileStickyFooter from "./subcomponents/MobileStickyFooter";
import SimilarListingsMiniTiles from "./subcomponents/SimilarListingsMiniTiles";

const ActivityEmailPreview = lazyWithRetry(
  () =>
    import(
      // @ts-ignore
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityEmailPreview.jsx"
    ),
);
const ActivityCharterSchoolEnrollPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityCharterSchoolEnrollPage"
    ),
);
const ActivityWaitlistPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityWaitlistPage"
    ),
);
const ActivityClassWalletEnrollPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "loggedInPages" */ "../../routes/ActivityClassWalletEnrollPage"
    ),
);

interface NotEnrolledViewProps {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
}

export default function NotEnrolledView(props: NotEnrolledViewProps) {
  const { currentUser, userTimeZone, esaSessionType } = useSession();
  const isClassWalletSession = esaSessionType === EsaSessionType.CW;
  const { path } = useRouteMatch() ?? {};
  const isMobile = Screen.useIsMobile();
  const { t } = useTranslation(
    "client\\components\\detailPage\\NotEnrolledView",
  );
  const navigate = useNavigation();
  const { activity, leader } = props;

  const isOrganizationEnrollment = isOrganizationAdminWhoCanEnroll(currentUser);
  const canManage =
    Boolean(activity) && Activity.canManage(activity, currentUser);
  const appState = useAppState();
  const canApprove = useCurrentUserHasRole(Roles.ClassApproval);
  const location = useLocation();
  const querySectionUid = location.query && location.query.sectionUid;
  const isOwnerOrAdmin =
    Activity.isOwner(activity, currentUser) || User.isAdmin(currentUser);
  const isRestricted = Boolean(activity?.isRestricted) && !isOwnerOrAdmin;
  const otherListingsRef = React.useRef(null);
  const moreLikeThisRef = React.useRef(null);
  const viewAvailabilityRef = React.useRef(null);
  const viewTheTeacherRef = React.useRef(null);

  const [openClassScheduleRequest, setOpenClassScheduleRequest] =
    React.useState(false);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] =
    React.useState(false);

  const scrollToElement = useScrollToElement();

  const trackProductViewed = useProductViewed();
  React.useEffect(() => {
    trackProductViewed({
      activity,
      leader,
    });

    const openRequestTimeModal =
      new URLSearchParams(window.location.search).get("openCSR") === "true";
    if (openRequestTimeModal && !isAvailabilityModalOpen) {
      setOpenClassScheduleRequest(openRequestTimeModal);
    }
  }, [
    activity,
    leader,
    trackProductViewed,
    navigate,
    isAvailabilityModalOpen,
    location,
  ]);

  const trackClassPageImpression = useTrackClassPageImpression();
  useEffect(() => {
    trackClassPageImpression({
      activityUid: activity.uid,
      sectionUid: querySectionUid,
    });
  }, [activity.uid, querySectionUid, trackClassPageImpression]);

  const onClickMoreLikeThis = React.useCallback(() => {
    if (isMobile && isAvailabilityModalOpen) {
      navigate(searchPath({ q: activity.title }));
    }
    scrollToElement(moreLikeThisRef.current || otherListingsRef.current);
  }, [
    navigate,
    scrollToElement,
    isMobile,
    isAvailabilityModalOpen,
    activity.title,
  ]);

  const onClickViewTeacher = React.useCallback(() => {
    scrollToElement(viewTheTeacherRef?.current);
  }, [scrollToElement]);

  const [activityWithEnrollableTimes, setActivityWithEnrollableTimes] =
    React.useState<ActivityEnrollableTimesModal_ActivityFragment | undefined>(
      undefined,
    );

  const esaState = useFundingProgramAbbr();
  const esaWaitPeriod = useEsaWaitPeriod();
  const fetchStartAfter = React.useMemo(() => {
    return !!esaState ? dayjs().add(esaWaitPeriod, "days").toDate() : undefined;
  }, [esaState, esaWaitPeriod]);

  const fetchEnrollableTimes = useFetchEnrollableTimes({
    startAfter: fetchStartAfter,
  });

  React.useEffect(() => {
    if (activity.hasTeacherSchedule) {
      fetchEnrollableTimes(activity.uid).then(a => {
        setActivityWithEnrollableTimes(a);
      });
    }
  }, [activity, fetchEnrollableTimes]);

  return (
    <SimpleLayout allowCategoriesNavbar>
      <ActivityMetaTags activity={activity} />
      {currentUser && (canManage || canApprove) && (
        <Toolbar>
          <Container sx={{ marginBottom: "medium" }}>
            <Box
              flex
              sx={{
                alignItems: "center",
                justifyContent: "center",
                marginRight: "2em",
              }}
            >
              <ActivityStatusLabel activity={activity} />
              <Space x="small" />
              {canManage ? (
                <Link
                  to={
                    activity.isClub
                      ? clubManageListingPath(activity)
                      : activitySchedulePath(activity)
                  }
                >
                  {t`Manage this class`}
                </Link>
              ) : canApprove ? (
                <Link to={activityApprovalPath(activity)}>
                  {t`Approve this class`}
                </Link>
              ) : (
                ""
              )}
            </Box>
          </Container>
        </Toolbar>
      )}
      {!activity.canEnroll && !activity.isSelfPaced && (
        <Box ref={moreLikeThisRef} key="moreLikeThis">
          <SimilarListingsMiniTiles activity={activity} />
        </Box>
      )}
      {!isRestricted && (
        <>
          {isMobile && (
            <MobileStickyFooter
              activity={activity}
              activityWithEnrollableTimes={activityWithEnrollableTimes}
              leader={leader}
              setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
            />
          )}
          {isMobile &&
            activity.upcomingAvailableSectionCount === 0 &&
            activity.upcomingSectionCount === 0 && (
              <ClassScheduleRequestModal
                activity={activity}
                user={currentUser}
                // @ts-ignore TS(2322): for practical purposes, userTimeZone is loaded by the time it's used
                userTimeZone={userTimeZone}
                open={openClassScheduleRequest}
                setOpen={setOpenClassScheduleRequest}
              />
            )}
          <ClassDetailsMainBody
            // data
            activity={activity}
            activityWithEnrollableTimes={activityWithEnrollableTimes}
            leader={leader}
            // scroll refs
            viewAvailabilityRef={viewAvailabilityRef}
            viewTheTeacherRef={viewTheTeacherRef}
            // event handlers
            onClickMoreLikeThis={onClickMoreLikeThis}
            onClickViewTeacher={onClickViewTeacher}
            isAvailabilityModalOpen={isAvailabilityModalOpen}
            setIsAvailabilityModalOpen={setIsAvailabilityModalOpen}
          />
          <Switch>
            <Route
              exact
              path={`${path}/enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location,
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : activity.hasTeacherSchedule ? (
                  <EnrollmentMeetingPurchasePage activityUid={activity.uid} />
                ) : isClassWalletSession ? (
                  <ActivityClassWalletEnrollPage activity={activity} />
                ) : (
                  <ActivityEnrollPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              path={`${path}/enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location,
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityEnrollPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              path={`${path}/waitlist`}
              render={routeProps => {
                const redirectPath = appState.getWaitlistPath(
                  routeProps.location,
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityWaitlistPage {...props} {...routeProps} />
                );
              }}
            />
            <Route
              exact
              path={`${path}/charter-enroll`}
              render={routeProps => {
                const redirectPath = appState.getEnrollPath(
                  routeProps.location,
                );

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  <ActivityCharterSchoolEnrollPage activity={activity} />
                );
              }}
            />
            <Route
              exact
              path={`${path}/email`}
              render={routeProps => {
                const redirectPath = appState.requireAuth(routeProps.location);

                return redirectPath ? (
                  <Redirect to={redirectPath} />
                ) : (
                  // @ts-ignore TS(2322): this is a jsx file, so we need to ignore the type error
                  <ActivityEmailPreview {...props} {...routeProps} />
                );
              }}
            />
            {!!currentUser && (
              <Route
                render={routeProps => {
                  const querySectionUid =
                    routeProps.location?.query?.sectionUid;
                  if (!!currentUser && querySectionUid && props.activity) {
                    const enrollPath = isOrganizationEnrollment
                      ? sectionCharterSchoolEnrollPath(
                          props.activity,
                          querySectionUid,
                        )
                      : sectionEnrollPath(props.activity, querySectionUid);
                    return <Redirect to={enrollPath} />;
                  }
                  return null;
                }}
              />
            )}
          </Switch>
        </>
      )}
      {!isMobile && !activity.isClub && (
        <DefaultBelowClassDetail
          otherListingsRef={otherListingsRef}
          activity={activity}
          leader={leader}
          moreLikeThisRef={moreLikeThisRef}
          isRestricted={isRestricted}
        />
      )}

      {isMobile && (
        <MobileBelowClassDetail
          otherListingsRef={otherListingsRef}
          activity={activity}
          leader={leader}
        />
      )}
    </SimpleLayout>
  );
}
